import React from 'react';
import {
  Box,
  Switch,
  Typography,
  FormControlLabel,
  type SwitchProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export interface ActiveStatusSwitchProps {
  label?: string;
  value: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  activeLabel?: string;
  inactiveLabel?: string;
  showLabels?: boolean;
}

// Custom styled switch with green/red colors
const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 58,
  height: 38,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(22px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M16.707,5.293a1,1,0,0,0-1.414,0L8,12.586,4.707,9.293a1,1,0,0,0-1.414,1.414l4,4a1,1,0,0,0,1.414,0l8-8A1,1,0,0,0,16.707,5.293Z"/></svg>')`,
      },
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.success.main,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.success.main,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#fff',
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#999',
      )}" d="M15.898,4.045a1.007,1.007,0,0,0-1.414,0L10,8.586,5.516,4.045a1,1,0,0,0-1.414,1.414L8.586,10,4.102,14.555a1,1,0,0,0,1.414,1.414L10,11.414l4.484,4.555a1,1,0,0,0,1.414-1.414L11.414,10l4.484-4.555A1.007,1.007,0,0,0,15.898,4.045Z"/></svg>')`,
    },
  },
  '& .MuiSwitch-track': {
    borderRadius: 20,
    backgroundColor: theme.palette.error.main,
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

/**
 * ActiveStatusSwitch Component
 * 
 * A reusable switch component for active/inactive status with custom styling:
 * - Green color when active (ON state)
 * - Red color when inactive (OFF state)
 * - Check icon when active, X icon when inactive
 * - Optional labels for active/inactive states
 * - Supports all standard Switch props
 */
const ActiveStatusSwitch: React.FC<ActiveStatusSwitchProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  size = 'medium',
  activeLabel = 'Activo',
  inactiveLabel = 'Inactivo',
  showLabels = true,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  const switchProps: Partial<SwitchProps> = {
    checked: value,
    onChange: handleChange,
    disabled,
    size,
  };

  if (label) {
    return (
      <FormControlLabel
        control={<StyledSwitch {...switchProps} />}
        label={label}
        disabled={disabled}
      />
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <StyledSwitch {...switchProps} />
      {showLabels && (
        <Typography
          variant="body2"
          color={value ? 'success.main' : 'error.main'}
          sx={{ fontWeight: 500, minWidth: 60 }}
        >
          {value ? activeLabel : inactiveLabel}
        </Typography>
      )}
    </Box>
  );
};

export default ActiveStatusSwitch;